import { isLoading } from "apollo/utils";
import clsx from "clsx";
import { FILE_SCOPE, QUERY_PROGRESS_NOT_STARTED } from "core/consts";
import { getStaticAsset } from "core/model/config";
import { formatUnixDate } from "core/model/utils/dates";
import { activateDTAAttachments } from "core/model/utils/featureFlags";
import {
  Auction,
  FileParams,
  FileState,
  Patient,
  QueryProgress,
} from "core/types";
import { Image, Spinner } from "ds/ui";
import { useHandleFileDownload } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import { useGetFilesFeatures } from "dsl/ecosystems/PatientFiles/useGetFilesFeatures";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import { FileTextIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "translations";

export const DocsModalSources = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const translations = useTranslations();
  const locale = useLocale();
  const [files, setFiles] = useState<{ name: string; file: FileState }[]>([]);

  const params: FileParams = activateDTAAttachments
    ? `auction_id=${auction.id}&scope=${FILE_SCOPE.PATIENT_FILES}`
    : `auction_id=${auction.id}`;

  const { getFilesHISProgress, queryProgress, uploadedFilesV2 } =
    useGetFilesFeatures({
      action: undefined,
      externalId: patient.external_id,
      params,
    });

  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [selectedFile, setSelectedFile] = useState<string | number | undefined>(
    undefined,
  );

  const handleDownloadFile = useHandleFileDownload({
    externalId: patient.external_id!,
    onComplete: () => setSelectedFile(undefined),
    setProgress,
  });

  useEffect(() => {
    (async () => {
      const filesData = await Promise.all(
        uploadedFilesV2?.map(async (file) => {
          const props = await getFileNameAndIsNewVersionAvailable({
            canAccessPatientData: true,
            file,
          });

          if (props.shouldBlur) {
            return null;
          }

          if (typeof props.value === "string" && file.updated_at) {
            return { file, name: props.value };
          }

          return null;
        }) ?? [],
      );
      if (filesData.truthy()) {
        setFiles(filesData.truthy());
      }
    })();
  }, [uploadedFilesV2]);

  return (
    <div className="flex w-[50%] flex-col border-l border-gray-400">
      <div className="sticky top-0 w-full p-4">
        <h2 className="text-heading-lg font-bold">
          {translations.extractDocs.splitView.sourcesTab.title}
        </h2>
      </div>
      <hr className="w-full border-gray-400" />
      <div
        className={clsx("flex h-[calc(95vh-140px)] w-full overflow-y-auto", {
          "bg-gray-50": !files.length,
        })}
      >
        {isLoading(getFilesHISProgress) || isLoading(queryProgress) ? (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner
              color="primary"
              size="lg"
              testId="spinner-loading-sources"
            />
          </div>
        ) : files.length ? (
          <ul className="flex w-full flex-col gap-4 p-4">
            {files.map(({ file, name }) => (
              <li key={`${name}-${file.updated_at}`} className="w-full">
                <button
                  className="flex w-full items-center justify-start gap-4 rounded-lg border border-gray-300 bg-white px-6 py-4 text-left"
                  type="button"
                  // todo: aria label
                  onClick={() => {
                    setSelectedFile(file.id);
                    handleDownloadFile(file);
                  }}
                >
                  {isLoading(progress) && selectedFile === file.id ? (
                    <Spinner
                      color="primary"
                      size="sm"
                      testId="spinner-loading-sources"
                    />
                  ) : (
                    <FileTextIcon className="h-6 w-6 text-primary" />
                  )}
                  <div className="flex flex-col gap-1 text-left">
                    <span className="text-body-md font-bold text-gray-900">
                      {name}
                    </span>
                    <span className="text-body-sm text-gray-500">
                      {formatUnixDate(file.updated_at!, locale)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-4">
            <Image
              alt={
                translations.extractDocs.splitView.sourcesTab.altTextEmptyState
              }
              className="h-[160px] w-auto overflow-visible"
              src={getStaticAsset("docs-empty-state.svg")}
            />
            <p className="w-[50%] text-center text-heading-md text-gray-600">
              {
                translations.extractDocs.splitView.sourcesTab
                  .emptyStateInformation
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
